import type { AxiosRequestConfig } from 'axios'
import type { EnvConfig } from 'src/configurations'
import { Environments } from 'src/configurations'
import {
  ExtendThreadIDHeader,
  RequestConfig,
} from 'src/queries/requestConfig.models'
import { requestContext } from 'src/queries/requestContext'
import { tokenStore } from 'src/queries/tokenStore'

export { RequestConfig } from 'src/queries/requestConfig.models'

/**
 * The single request-config boundary for query network functions, built
 * entirely from non-Redux sources: tokens from `tokenStore`, hosts/brand/
 * platform inputs from `requestContext` (`__APP_CONFIG__`/`__BRAND_CONFIG__`
 * on the browser, the per-request snapshot on the server). Every value below
 * is read at call time — never cached at module scope — so server-side calls
 * resolve through the per-request AsyncLocalStorage contexts.
 *
 * The header/host derivations intentionally match the legacy
 * `axios.selectors` byte-for-byte (including the host fallback literals);
 * the parity oracle is `src/queries/__tests__/requestConfig.parity.test.ts`.
 * The legacy selectors remain for the Redux thunk files scheduled for
 * deletion by the RQ-migration pillar.
 */

const DEFAULT_LOCAL_USER_ID = 'u_3W54BGASjSQ81Ta6Q5Yyhg' // from Flyway seed migration

const getLocalUserIdOverride = (): string | null => {
  try {
    return typeof window !== 'undefined'
      ? localStorage.getItem('LOCAL_USER_ID_OVERRIDE')
      : null
  } catch {
    return null
  }
}

// Memoized per envConfig identity, matching the legacy reselect semantics: the
// localStorage override is read once per config lifetime, not once per request.
let localUserIdCacheKey: EnvConfig | undefined
let localUserIdCache: string | undefined

const resolveLocalUserId = (envConfig: EnvConfig): string | undefined => {
  if (envConfig !== localUserIdCacheKey) {
    const environment = envConfig.ENVIRONMENT || Environments.Dev
    const localAuthBypass =
      environment !== Environments.Prod &&
      environment !== Environments.Stage &&
      envConfig.LOCAL_AUTH_BYPASS === 'true'
    localUserIdCache = localAuthBypass
      ? getLocalUserIdOverride() ||
        envConfig.LOCAL_USER_ID ||
        DEFAULT_LOCAL_USER_ID
      : undefined
    localUserIdCacheKey = envConfig
  }
  return localUserIdCache
}

type RequestHeaders = Record<string, string | undefined>

const baseRequestHeaders = (): RequestHeaders => {
  const brandConfig = requestContext.getBrandConfig()
  return {
    'X-Extend-Brand': brandConfig.BRAND_ID,
    'X-Extend-App-ID': brandConfig.PRIMARY_DOMAIN,
    'X-Extend-Platform':
      requestContext.getIsCspAuthenticated() ||
      requestContext.getPathname().includes('/csp/')
        ? 'mira'
        : 'web',
    'X-Extend-Platform-Version':
      typeof navigator !== 'undefined' ? navigator.userAgent : '',
    // until org switcher is built out, hard code the org id into this header
    // 'X-Extend-Organization': '',
    Accept: 'application/vnd.paywithextend.v2021-03-12+json',
  }
}

const unauthedJsonRequestHeaders = (): RequestHeaders => ({
  ...baseRequestHeaders(),
  'Content-Type': 'application/json',
})

const unauthedFormRequestHeaders = (): RequestHeaders => ({
  ...baseRequestHeaders(),
  'Content-Type': 'multipart/form-data',
})

const withPrimarySessionAuth = (headers: RequestHeaders): RequestHeaders => {
  const jwt = tokenStore.getAccessToken()
  const bearerHeaders =
    jwt === undefined ? headers : { ...headers, Authorization: `Bearer ${jwt}` }
  const localUserId = resolveLocalUserId(requestContext.getEnvConfig())
  return localUserId
    ? { ...bearerHeaders, 'X-Local-User-Id': localUserId }
    : bearerHeaders
}

const cspAuthedJsonRequestHeaders = (): RequestHeaders => {
  const cspJwt = tokenStore.getCspJwt()
  const headers = unauthedJsonRequestHeaders()
  return cspJwt === undefined
    ? headers
    : { ...headers, Authorization: `Bearer ${cspJwt}` }
}

const apiHost = (envConfig: EnvConfig): string => {
  // Server-side, API_HOST_INTERNAL lets prefetch axios calls reach extend-api
  // via a Docker-internal hostname; it is never serialized to the client.
  if (typeof window === 'undefined') {
    const internal = process.env.API_HOST_INTERNAL
    if (internal) return internal
  }
  return envConfig.API_HOST || 'https://api-stage.paywithextend.com'
}

export const getRequestConfig = (
  config?: RequestConfig,
): AxiosRequestConfig => {
  const envConfig = requestContext.getEnvConfig()

  switch (config) {
    case RequestConfig.Vault:
      return {
        headers: withPrimarySessionAuth(unauthedJsonRequestHeaders()),
        baseURL: envConfig.VAULT_HOST || 'https://v-stage.paywithextend.com',
      }
    case RequestConfig.Unauthed:
      return {
        headers: unauthedJsonRequestHeaders(),
        baseURL: apiHost(envConfig),
      }
    case RequestConfig.Form:
      return {
        headers: withPrimarySessionAuth(unauthedFormRequestHeaders()),
        baseURL: apiHost(envConfig),
      }
    case RequestConfig.Agent:
      return {
        headers: {
          ...withPrimarySessionAuth(unauthedJsonRequestHeaders()),
          [ExtendThreadIDHeader]: '',
        },
        baseURL:
          envConfig.AGENT_HOST ||
          'https://muppeteer-stage.paywithextend.com/api/v1',
      }
    case RequestConfig.Addressulator:
      return {
        headers: {
          'Content-Type': 'application/json',
        },
        baseURL:
          envConfig.ADDRESSULATOR_HOST || 'https://address-stage.extend.dev',
      }
    case RequestConfig.Csp:
      return {
        headers: cspAuthedJsonRequestHeaders(),
        baseURL: apiHost(envConfig),
      }
    case RequestConfig.Default:
    default:
      return {
        headers: withPrimarySessionAuth(unauthedJsonRequestHeaders()),
        baseURL: apiHost(envConfig),
      }
  }
}
